import type { UseQueryOptions, UseQueryResult } from 'react-query';
import { useQueries, useQuery } from 'react-query';
import { getData } from '../api/api';
import { QuantzQueryCacheDuration } from '../model';
import type { QuantzRequest, QuantzResponse } from '../model';

const QUANTZ_QUERY_KEY = 'quantz-query';
// We cache the request for few second to mitigate the problem when we rerender too much time
const CACHE_TIME = QuantzQueryCacheDuration.Tiny;

export const generateQuantzQueryOptions = (
  request: QuantzRequest,
  enabled = true,
  cacheTime: QuantzQueryCacheDuration = CACHE_TIME,
): UseQueryOptions<QuantzResponse, Error, QuantzResponse> => {
  return {
    queryKey: `${QUANTZ_QUERY_KEY}-${JSON.stringify(request)}-${cacheTime}`,
    queryFn: () => getData(request),
    cacheTime,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    enabled,
  };
};

export const useQuantzData = (
  request: QuantzRequest,
  enabled = true,
  cacheTime?: QuantzQueryCacheDuration,
): UseQueryResult<QuantzResponse, Error> => {
  return useQuery<QuantzResponse, Error, QuantzResponse>(
    generateQuantzQueryOptions(request, enabled, cacheTime),
  );
};

export const useQuantzDataQueries = (
  requests: QuantzRequest[],
  enabled = true,
  cacheTime?: QuantzQueryCacheDuration,
): UseQueryResult<QuantzResponse, Error>[] => {
  const queries = requests.map(
    (request) => generateQuantzQueryOptions(request, enabled, cacheTime),
    // The type inference is fixed in react query v3.28.0
    // https://github.com/TanStack/query/issues/1675
  ) as UseQueryOptions<unknown, unknown, unknown>[];

  return useQueries(queries) as UseQueryResult<QuantzResponse, Error>[];
};
